import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart Upsell — carrossel de produtos recomendados no cart drawer.
 *
 * O markup base é renderizado pelo snippet `cart-upsell.liquid` dentro do
 * cart drawer. Os cards são montados no cliente a partir da API de
 * recomendações da Shopify. Como o Horizon re-renderiza o drawer via morph
 * a cada `cart:update`, o componente se reconstrói após cada atualização.
 */

function formatMoney(cents) {
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function getOptionName(product, position) {
  const option = product.options && product.options[position - 1];
  if (!option) return `Opção ${position}`;
  return typeof option === 'string' ? option : option.name || `Opção ${position}`;
}

// Valores distintos de uma opção, extraídos das variantes (independe do
// formato de `options` retornado pela API de recomendações).
function getOptionValues(variants, position) {
  const values = [];
  variants.forEach((variant) => {
    const value = variant['option' + position];
    if (value != null && !values.includes(value)) values.push(value);
  });
  return values;
}

function createProductCard(product) {
  if (!product.variants || !product.variants.length) return '';
  const defaultVariant = product.variants.find((v) => v.available) || product.variants[0];
  const truncatedTitle = product.title.length > 22 ? `${product.title.slice(0, 22)}...` : product.title;

  const compareAt = defaultVariant.compare_at_price;
  const showCompare = compareAt && compareAt > defaultVariant.price;
  const compareHtml = `<span class="cu-card__compare"${showCompare ? '' : ' hidden'}>${
    showCompare ? formatMoney(compareAt) : ''
  }</span>`;

  // Um <select> por opção que tenha mais de um valor.
  let selectorsHtml = '';
  if (product.variants.length > 1) {
    const optionCount =
      (product.options && product.options.length) ||
      product.variants.reduce((max, v) => Math.max(max, (v.options || []).length), 0);
    for (let position = 1; position <= optionCount; position++) {
      const values = getOptionValues(product.variants, position);
      if (values.length <= 1) continue;
      const name = getOptionName(product, position);
      const optionsHtml = values
        .map((value) => {
          const selected = defaultVariant['option' + position] === value ? ' selected' : '';
          return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(value)}</option>`;
        })
        .join('');
      selectorsHtml += `
        <label class="cu-card__option">
          <span class="visually-hidden">${escapeHtml(name)}</span>
          <select class="cu-card__select" data-position="${position}" aria-label="${escapeHtml(name)}">${optionsHtml}</select>
        </label>`;
    }
  }

  const variantData = product.variants.map((v) => ({
    id: v.id,
    available: v.available,
    price: v.price,
    compare_at_price: v.compare_at_price,
    option1: v.option1,
    option2: v.option2,
    option3: v.option3,
  }));

  const addLabel = `Adicionar ${product.title} ao carrinho`;

  const optionsBlock = selectorsHtml ? `<div class="cu-card__options">${selectorsHtml}</div>` : '';

  const image = product.featured_image || '';
  const imageHtml = image
    ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" class="cu-card__image" loading="lazy">`
    : `<span class="cu-card__image cu-card__image--placeholder" aria-hidden="true"></span>`;

  // Título vira link para a página do produto quando a URL está disponível
  // (API de recomendações e modo manual fornecem `url`).
  const titleHtml = product.url
    ? `<a href="${escapeHtml(product.url)}" class="cu-card__name-link">${escapeHtml(truncatedTitle)}</a>`
    : escapeHtml(truncatedTitle);

  return `
    <div class="cu-card" data-variants="${escapeHtml(JSON.stringify(variantData))}">
        <div class="cu-card__inner">
            ${imageHtml}
            <div class="cu-card__details">
                <h3 class="cu-card__name" title="${escapeHtml(product.title)}">${titleHtml}</h3>
                <p class="cu-card__price"><span class="cu-card__price-current">${formatMoney(
                  defaultVariant.price
                )}</span>${compareHtml}</p>
                ${optionsBlock}
            </div>
            <button type="button" class="cu-card__add" data-id="${defaultVariant.id}" data-label="${escapeHtml(
              addLabel
            )}" aria-label="${escapeHtml(addLabel)}"${defaultVariant.available ? '' : ' disabled'}>+</button>
        </div>
    </div>
  `;
}

// Cards com variantes: resolve a variante a partir dos <select>s e
// sincroniza preço, preço comparativo e o botão de adicionar.
function wireVariantSelectors(card) {
  const selects = [...card.querySelectorAll('.cu-card__select')];
  if (!selects.length) return;

  let variants = [];
  try {
    variants = JSON.parse(card.dataset.variants || '[]');
  } catch (e) {
    return;
  }

  const button = card.querySelector('.cu-card__add');
  const priceCurrent = card.querySelector('.cu-card__price-current');
  const priceCompare = card.querySelector('.cu-card__compare');

  const selected = () => {
    const chosen = {};
    selects.forEach((select) => {
      chosen[select.dataset.position] = select.value;
    });
    return chosen;
  };

  const matchAvailable = (criteria) =>
    variants.some(
      (variant) =>
        variant.available && Object.keys(criteria).every((pos) => String(variant['option' + pos]) === criteria[pos])
    );

  const findVariant = (chosen) => {
    const matches = variants.filter((variant) =>
      Object.keys(chosen).every((pos) => String(variant['option' + pos]) === chosen[pos])
    );
    return matches.find((v) => v.available) || matches[0] || null;
  };

  // Desabilita valores que não formam variante disponível dado o que está
  // escolhido nos outros selects, e marca como "(esgotado)".
  const refreshAvailability = () => {
    const chosen = selected();
    selects.forEach((select) => {
      const pos = select.dataset.position;
      [...select.options].forEach((option) => {
        const base = option.dataset.label || (option.dataset.label = option.textContent);
        const available = matchAvailable({ ...chosen, [pos]: option.value });
        option.disabled = !available;
        option.textContent = available ? base : `${base} (esgotado)`;
      });
    });
  };

  const resolve = () => {
    refreshAvailability();
    const variant = findVariant(selected());

    if (!variant) {
      button.disabled = true;
      button.setAttribute('aria-label', 'Indisponível');
      return;
    }

    button.dataset.id = variant.id;
    button.disabled = !variant.available;
    button.setAttribute('aria-label', variant.available ? button.dataset.label : 'Indisponível');

    priceCurrent.textContent = formatMoney(variant.price);
    if (priceCompare) {
      const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceCompare.textContent = showCompare ? formatMoney(variant.compare_at_price) : '';
      priceCompare.hidden = !showCompare;
    }
  };

  selects.forEach((select) => select.addEventListener('change', resolve));
  resolve();
}

class CartUpsellComponent extends HTMLElement {
  #index = 0;
  #building = false;
  #hadCards = false;
  #observer = null;
  #stickyResizeObserver = null;
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Eventos delegados sobrevivem ao morph (o morph troca apenas os filhos;
    // este elemento e seus listeners permanecem).
    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const prev = event.target.closest('.cu-arrow--prev');
        const next = event.target.closest('.cu-arrow--next');
        const add = event.target.closest('.cu-card__add');
        if (prev) {
          event.preventDefault();
          this.#go(-1);
        }
        if (next) {
          event.preventDefault();
          this.#go(1);
        }
        if (add) {
          event.preventDefault();
          this.#addToCart(add);
        }
      },
      { signal }
    );

    // Após qualquer atualização de carrinho o tema faz morph da seção, o que
    // esvazia o track — reconstrói na sequência.
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      () => {
        setTimeout(() => this.refresh(), 50);
      },
      { signal }
    );

    // Cobre re-renderizações assíncronas (section-renderer) que esvaziam o
    // track fora da janela do setTimeout acima. Só reage se havia cards
    // montados, evitando loops quando não há recomendações.
    this.#observer = new MutationObserver(() => {
      if (this.#building || !this.#hadCards) return;
      const track = this.querySelector('.cu-track');
      if (track && !track.children.length) this.refresh();
    });
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#setupStickyOffset();
    this.refresh();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#stickyResizeObserver?.disconnect();
  }

  // Mantém o carrossel colado logo acima do resumo (que é sticky no rodapé do
  // drawer), sem mexer no resumo: espelhamos a altura do resumo como offset do
  // nosso próprio `position: sticky` (--cu-sticky-bottom).
  #setupStickyOffset() {
    const content = this.closest('.cart-drawer__content');
    const summary = content?.querySelector('.cart-drawer__summary');
    if (!summary || typeof ResizeObserver === 'undefined') return;

    const sync = () => {
      this.style.setProperty('--cu-sticky-bottom', `${summary.offsetHeight}px`);
    };

    this.#stickyResizeObserver = new ResizeObserver(sync);
    this.#stickyResizeObserver.observe(summary);
    sync();
  }

  async refresh() {
    if (this.#building) return;
    this.#building = true;
    try {
      const cart = await this.#fetchCart();
      const track = this.querySelector('.cu-track');
      if (!cart?.items?.length || !track) {
        this.hidden = true;
        this.#hadCards = false;
        return;
      }

      const products = await this.#fetchProducts(cart);

      const cards = (products || [])
        .filter((product) => !cart.items.some((item) => item.product_id === product.id))
        .map((product) => createProductCard(product))
        .join('');

      track.innerHTML = cards;

      if (!track.children.length) {
        this.hidden = true;
        this.#hadCards = false;
        return;
      }

      [...track.querySelectorAll('.cu-card')].forEach((card) => wireVariantSelectors(card));
      this.#index = 0;
      this.#show(0);
      this.hidden = false;
      this.#hadCards = true;
    } catch (error) {
      console.error('Cart upsell:', error);
      this.hidden = true;
      this.#hadCards = false;
    } finally {
      this.#building = false;
    }
  }

  #show(i) {
    const cards = this.querySelectorAll('.cu-card');
    cards.forEach((card, n) => card.classList.toggle('cu-card--active', n === i));
  }

  #go(direction) {
    const cards = this.querySelectorAll('.cu-card');
    if (!cards.length) return;
    this.#index = (this.#index + direction + cards.length) % cards.length;
    this.#show(this.#index);
  }

  async #fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error('Falha ao buscar o carrinho');
    return response.json();
  }

  /**
   * Retorna os produtos do carrossel conforme a origem configurada
   * (`data-mode`, vindo de settings.cart_upsell_mode):
   *
   * - auto:   recomendações da Shopify a partir do primeiro item do carrinho.
   * - manual: apenas a lista embutida pelo snippet.
   * - hybrid: lista manual primeiro, seguida das recomendações (sem repetir
   *           produtos que já estão na lista manual).
   */
  async #fetchProducts(cart) {
    const mode = this.dataset.mode || 'auto';

    let manual = [];
    const manualData = this.querySelector('[data-cart-upsell-products]');
    if (manualData) {
      try {
        manual = JSON.parse(manualData.textContent) || [];
      } catch (error) {
        console.error('Cart upsell: JSON de produtos manuais inválido', error);
      }
    }

    if (mode === 'manual') return manual;

    let recommended = [];
    try {
      const productId = cart.items[0].product_id;
      const response = await fetch(
        `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=20&intent=related`
      );
      const { products } = await response.json();
      recommended = products || [];
    } catch (error) {
      // No modo híbrido a lista manual ainda pode ser exibida sozinha.
      console.error('Cart upsell: falha ao buscar recomendações', error);
    }

    if (mode === 'hybrid' && manual.length) {
      const manualIds = new Set(manual.map((product) => product.id));
      return [...manual, ...recommended.filter((product) => !manualIds.has(product.id))];
    }

    return recommended;
  }

  async #addToCart(button) {
    if (!button || button.disabled) return;
    const variantId = button.getAttribute('data-id');
    if (!variantId) return;

    button.disabled = true;

    // Pede as seções dos cart-items-component (mesmo fluxo do product-form
    // do Horizon) para o drawer atualizar via morph.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    // Contrato do tema: dispara CartLinesUpdateEvent com uma promise que, ao
    // resolver, entrega as `sections` re-renderizadas. O cart-items-component
    // (e cart-icon, header-actions) escutam esse evento e fazem o morph/refresh.
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await response.json();
      if (added.status) throw new Error(added.message || 'Falha ao adicionar ao carrinho');

      const cart = await this.#fetchCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'cart-upsell-component',
          sections: added.sections,
          itemCount: cart.item_count,
        },
      });
    } catch (error) {
      console.error('Cart upsell:', error);
      deferredEventPromise.reject(error);
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
